import { useContext, useEffect, useRef, useState } from "react"
import language from "../../../language/language"
import { LanguageContext } from "../../../utilities/contexts/LanguageContext"
import { DataGrid } from "@mui/x-data-grid"
import { Box, Chip, Stack, Typography } from "@mui/material"
import { AddressInfo, CustomerDetails, CustomerResponse, formatARNumber, formatPhoneNumber } from "../../../utilities/services/CustomerService/CustomerService"
import { invoiceListStyles } from "../../RouteBuilder/components/InvoiceList/invoiceListStyles"
import Colors from "../../../assets/Colors"
import React from "react"
import { StoreContext } from '../../../utilities/contexts/StoreContext'

type CustomerDetailsListProp = {
    page: number
    pageSize: number
    customerDetails: CustomerResponse
    handlePageChange: (event: any) => void,
    handleRowsPerPageChange: (event: any) => void,
    onCustomerDetailsClick: (event: CustomerDetails) => void
}

export const getColorBasedOnServiceLevel = (serviceLevel: any, isCompanyOwned: boolean) => {
  let colorCode = '#ADADAD'
  switch (true) {
    case serviceLevel === 'PLATINUM':
      colorCode = '#D6D6D6'
      break
    case serviceLevel === 'GOLD':
      colorCode = '#FFE395'
      break
    case serviceLevel === 'SILVER':
      colorCode = '#ADADAD'
      break
    case serviceLevel === 'BRONZE' && !isCompanyOwned:
      colorCode = '#E49600'
      break
    case serviceLevel === 'AUTODASH':
      colorCode = '#fe5600'
      break
  }
  return colorCode
}

export function getAddressDisplay(address?: AddressInfo[]) {
  if (!!address) {
    if (address.length === 1) {
      const adr = address[0];
      return (
        <Box display="flex" flexDirection="column" textAlign="left">
          <Typography>{adr.addressline1}</Typography>
          <Typography>{adr.city}, {adr.state} {adr.zip}</Typography>
        </Box>
      );
    }
    if (address.length > 1) {
      return <Typography sx={{color:'blue'}}>Multiple Locations</Typography>;
    }
    return <Typography sx={{color:'black'}}>Add Location</Typography>;
  }
  return null;
}

const CustomerDetailsList = ({page, pageSize, customerDetails, handlePageChange, handleRowsPerPageChange, onCustomerDetailsClick}: CustomerDetailsListProp) => {
    const windowSize = useRef([window.innerWidth, window.innerHeight])
    const { currentLanguage } = useContext(LanguageContext)
    const [tableData, setTableData] = useState<any[]>([]);
    const { isCompanyOwned } = useContext(StoreContext)

    const columns = [
        {
            field: 'name',
            headerName: `${language[currentLanguage].nameText}`,
            flex: 1,
            editable: false
        },
        {
            field: 'arNumber',
            headerName: `${language[currentLanguage].ar}`,
            flex: 1,
            editable: false,
        },
        {
            field: 'phone',
            headerName: `${language[currentLanguage].phone}`,
            flex: 1,
            editable: false,
        },
        {
            field: 'serviceLevel',
            headerName: `${language[currentLanguage].priorityInMins}`,
            flex: 1,
            editable: false,
            renderCell: (params: any) => {
                return <Chip
                style={{
                  height: '24px',
                  width: '100px',
                  borderRadius: '3px',
                  backgroundColor: getColorBasedOnServiceLevel(params.row.serviceLevel, isCompanyOwned),
                  fontSize: '12px',
                  color: params.row.serviceLevel !== "AUTODASH" ? Colors.napaGrey2 : Colors.white
                }}
                label={
                  (!!params.row.serviceLevel && params.row.serviceLevel !== 'BRONZE') ? params.row.serviceLevel : 'SILVER'
                }
              />
            }
        },
        {
            field: 'address',
            headerName: `${language[currentLanguage].address}`,
            flex: 1,
            editable: false,
            renderCell: (params: any) => getAddressDisplay(params.row.address)
        }
    ]

    const onRowClick = (event: any) => {
        const data = customerDetails?.commercialCustomerListInfo?.find(customerDetail=>{
            return customerDetail != null && customerDetail?.customerInfo?.customerId === event?.id
        });
        if (data) onCustomerDetailsClick(data)
    }

    useEffect(() => {
        if (!customerDetails) return;
        const _tableData = customerDetails?.commercialCustomerListInfo?.filter(customerDetail=>{
            return customerDetail != null
        }).map(customerDetail=>{
            return {
                id: customerDetail?.customerInfo?.customerId,
                name: customerDetail?.customerInfo?.name,
                arNumber: formatARNumber(customerDetail?.customerInfo?.accountInfo?.[0]?.accountReceivableNumber?.[0]),
                phone: formatPhoneNumber(customerDetail?.customerInfo?.contactInfo?.[0]?.phoneNumber?.[0]+""),
                serviceLevel: customerDetail?.customerInfo?.priority?.toUpperCase(),
                address: customerDetail?.addressInfo
            }
        })
        setTableData(_tableData || []);
    }, [customerDetails]) 

    const rowCountRef = React.useRef(customerDetails?.pageDetailsInfo?.totalRecords || 0);

    const rowCount = React.useMemo(() => {
      if (!!customerDetails?.pageDetailsInfo?.totalRecords) {
        rowCountRef.current = customerDetails.pageDetailsInfo.totalRecords;
      }
      return rowCountRef.current;
    }, [customerDetails?.pageDetailsInfo?.totalRecords]);

    return <DataGrid 
      style={{ height: `${windowSize.current[1] - 200}px` }}
      columns={columns} 
      rows={tableData}
      page={page-1}
      pageSize={pageSize}
      paginationMode="server"
      rowCount={rowCount}
      hideFooterPagination={tableData.length === 0}
      sx={invoiceListStyles.gridSize}
      components={{
          NoRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                {language[currentLanguage].noResultsCustomerView}
              </Stack>
            )
      }}
      onPageChange={handlePageChange}
      onPageSizeChange={handleRowsPerPageChange}
      onRowClick={onRowClick}
      ></DataGrid>
}

export default CustomerDetailsList